import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { GridCloseIcon } from '@mui/x-data-grid';
import qs from 'qs';
import { useToast } from 'use-toast-mui';

import { getAllTickets, getAllPass, getAllTrial } from '../../../api/ticket';
import { errorMessages, formatterCurrency } from '../../../utils/methods';
import {
  PickerContainer,
  PickerHeader,
  PickerTitle,
  PickerSubText,
  PickerCloseIcon,
  PickerSearchRow,
  PickerSearchInput,
  PickerSearchButton,
  PickerListWrapper,
  PickerRow,
  PickerRowMain,
  PickerRowName,
  PickerRowMeta,
  PickerRowPrice,
  PickerEmpty,
  PickerMoreButton,
} from './ProductPickerModalStyled';

const PAGE_SIZE = 20;

// 상품 종류별 조회 함수와 필드 매핑. 세 목록 API 가 같은 쿼리 형태(page/limit/이름/branchId)를 쓴다.
// idKey 는 목록 응답이 실제로 내려주는 식별자 필드다. 이용권만 coursePassId 로 다르다.
const KIND_CONFIG = {
  TICKET: { label: '수강권', fetch: getAllTickets, nameKey: 'ticketName', idKey: 'id' },
  PASS: { label: '이용권', fetch: getAllPass, nameKey: 'passName', idKey: 'coursePassId' },
  TRIAL: { label: '일일권', fetch: getAllTrial, nameKey: 'trialName', idKey: 'id' },
};

/**
 * 기존 상품을 검색해 고르는 모달.
 * 고른 상품의 id 를 onSelect 로 넘기면 호출부가 ?copyFrom={id} 로 이동해 복사 로직을 그대로 탄다.
 */
const ProductPickerModal = ({ open, kind, onClose, onSelect }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);

  const config = KIND_CONFIG[kind];

  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (targetPage, searchWord) => {
      if (!config) return;
      setLoading(true);
      try {
        const queryString = qs.stringify({
          page: targetPage,
          limit: PAGE_SIZE,
          sortOrder: 'DESCENDING',
          sortType: 'LATEST',
          [config.nameKey]: searchWord,
          branchId: user?.branch?.branchId,
          headBranchId: user?.headBranch?.headBranchId,
        });
        const res = await config.fetch(queryString, user.token);
        setRows(prev => (targetPage === 1 ? res?.content ?? [] : [...prev, ...(res?.content ?? [])]));
        setTotalElements(res?.totalElements ?? 0);
      } catch (error) {
        toast.error(errorMessages(error));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, user?.branch?.branchId, user?.headBranch?.headBranchId, user?.token],
  );

  // 열릴 때마다 검색 상태를 초기화하고 첫 페이지를 받아온다
  useEffect(() => {
    if (!open) return;
    setKeyword('');
    setPage(1);
    load(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kind]);

  const handleSearch = () => {
    setPage(1);
    load(1, keyword);
  };

  const handleMore = () => {
    const next = page + 1;
    setPage(next);
    load(next, keyword);
  };

  if (!config) return null;

  const periodText = row =>
    `${row.usagePeriod ?? 0}${row.usagePeriodType === 'MONTH' ? '개월' : '일'}`;

  return (
    <Modal open={open} onClose={onClose}>
      <PickerContainer>
        <PickerCloseIcon as={GridCloseIcon} onClick={onClose} />
        <PickerHeader>
          <PickerTitle>불러올 {config.label} 선택</PickerTitle>
          <PickerSubText>
            고른 {config.label}의 설정을 그대로 가져와 새 상품을 만듭니다. 판매종료된 상품도 고를 수 있습니다.
          </PickerSubText>
        </PickerHeader>

        <PickerSearchRow>
          <PickerSearchInput
            placeholder={`${config.label}명을 검색해 주세요.`}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
          <PickerSearchButton onClick={handleSearch} variant="contained" disableElevation>
            <SearchIcon sx={{ width: '18px', height: '18px' }} />
            검색
          </PickerSearchButton>
        </PickerSearchRow>

        <PickerListWrapper>
          {rows.length === 0 && !loading && <PickerEmpty>검색 결과가 없습니다.</PickerEmpty>}

          {rows.map(row => (
            <PickerRow key={row[config.idKey]} onClick={() => onSelect(row[config.idKey])}>
              <PickerRowMain>
                <PickerRowName>{row[config.nameKey]}</PickerRowName>
                <PickerRowMeta>
                  {[
                    row.classType ? t(row.classType) : null,
                    row.sportTypes?.length ? row.sportTypes.map(s => t(s)).join(', ') : row.sportType ? t(row.sportType) : null,
                    periodText(row),
                    row.usageCount ? `${row.usageCount}회` : null,
                    row.saleStatus === 'APPROVED' ? null : '판매종료',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </PickerRowMeta>
              </PickerRowMain>
              <PickerRowPrice>{formatterCurrency(row.finalPrice)}원</PickerRowPrice>
            </PickerRow>
          ))}

          {rows.length > 0 && rows.length < totalElements && (
            <PickerMoreButton onClick={handleMore} disabled={loading}>
              더 보기 ({rows.length}/{totalElements})
            </PickerMoreButton>
          )}
        </PickerListWrapper>
      </PickerContainer>
    </Modal>
  );
};

export default ProductPickerModal;
